import React, { useState, useRef, useCallback } from 'react';
import { documentAPI, knowledgeBaseAPI } from '../../services/api';
import { KnowledgeBase } from '@shared/types';
import './PDFUpload.css';

interface UploadProgress {
    documentId: string;
    status: 'uploading' | 'processing' | 'completed' | 'failed';
    progress: number;
    message: string;
}

interface DocumentRecord {
    id: string;
    knowledge_base_id: string;
    filename: string;
    file_size: number;
    file_type: string;
    status: string;
    processing_progress: number;
    created_at: string;
}

const PDFUpload: React.FC = () => {
    const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
    const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<string>('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [description, setDescription] = useState<string>('');
    const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
    const [documents, setDocuments] = useState<DocumentRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    // 加载知识库列表
    const loadKnowledgeBases = useCallback(async () => {
        try {
            const result = await knowledgeBaseAPI.getKnowledgeBases();
            if (result.success && result.data) {
                setKnowledgeBases(result.data);
            }
        } catch (error) {
            console.error('加载知识库失败:', error);
            setError('加载知识库失败，请稍后再试');
        }
    }, []);

    // 加载文档列表
    const loadDocuments = useCallback(async (knowledgeBaseId: string) => {
        if (!knowledgeBaseId) return;

        try {
            const result = await documentAPI.getKnowledgeBaseDocuments(knowledgeBaseId);
            if (result.success && result.documents) {
                setDocuments(result.documents);
            }
        } catch (error) {
            console.error('加载文档列表失败:', error);
        }
    }, []);

    // 组件挂载时加载数据
    React.useEffect(() => {
        loadKnowledgeBases();
    }, [loadKnowledgeBases]);

    // 选择知识库时加载对应文档
    React.useEffect(() => {
        if (selectedKnowledgeBase) {
            loadDocuments(selectedKnowledgeBase);
        } else {
            setDocuments([]);
        }
    }, [selectedKnowledgeBase, loadDocuments]);

    // 文件选择处理
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // 验证文件类型
            if (file.type !== 'application/pdf') {
                setError('请选择PDF格式的文件');
                return;
            }

            // 验证文件大小 (10MB)
            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                setError('文件大小不能超过10MB');
                return;
            }

            setSelectedFile(file);
            setError('');
            setSuccess('');
        }
    };

    // 拖拽处理
    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const files = event.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];

            if (file.type !== 'application/pdf') {
                setError('请拖拽PDF格式的文件');
                return;
            }

            const maxSize = 10 * 1024 * 1024;
            if (file.size > maxSize) {
                setError('文件大小不能超过10MB');
                return;
            }

            setSelectedFile(file);
            setError('');
            setSuccess('');
        }
    };

    // 上传文件
    const handleUpload = async () => {
        if (!selectedFile || !selectedKnowledgeBase) {
            setError('请选择文件和知识库');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('knowledgeBaseId', selectedKnowledgeBase);
            if (description.trim()) {
                formData.append('description', description.trim());
            }

            // 上传文件
            setUploadProgress({
                documentId: '',
                status: 'uploading',
                progress: 10,
                message: '正在上传文件...'
            });

            const uploadResult = await documentAPI.uploadDocument(formData);

            if (uploadResult.success && uploadResult.document_id) {
                setUploadProgress({
                    documentId: uploadResult.document_id,
                    status: 'processing',
                    progress: 50,
                    message: uploadResult.message || '文档上传成功，正在处理中...'
                });

                setSuccess('文档上传成功！');

                // 重新加载文档列表
                loadDocuments(selectedKnowledgeBase);

                // 清理表单
                setSelectedFile(null);
                setDescription('');
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

                // 模拟进度更新
                setTimeout(() => {
                    setUploadProgress({
                        documentId: uploadResult.document_id!,
                        status: 'completed',
                        progress: 100,
                        message: '处理完成！'
                    });

                    setTimeout(() => {
                        setUploadProgress(null);
                    }, 2000);
                }, 1000);

            } else {
                setError(uploadResult.error || '上传失败');
                setUploadProgress(null);
            }
        } catch (error) {
            console.error('上传失败:', error);
            setError('网络错误，请稍后再试');
            setUploadProgress(null);
        } finally {
            setLoading(false);
        }
    };

    // 删除文档
    const handleDeleteDocument = async (documentId: string) => {
        if (!confirm('确定要删除这个文档吗？')) {
            return;
        }

        try {
            const result = await documentAPI.deleteDocument(documentId);
            if (result.success) {
                setSuccess('文档删除成功');
                // 重新加载文档列表
                loadDocuments(selectedKnowledgeBase);
            } else {
                setError(result.error || '删除失败');
            }
        } catch (error) {
            console.error('删除失败:', error);
            setError('删除失败，请稍后再试');
        }
    };

    // 格式化文件大小
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // 格式化日期
    const formatDate = (dateString: string): string => {
        return new Date(dateString).toLocaleString('zh-CN');
    };

    // 获取状态文本
    const getStatusText = (status: string): string => {
        const statusMap: { [key: string]: string } = {
            'pending': '等待处理',
            'uploaded': '已上传',
            'processing': '处理中',
            'completed': '已完成',
            'failed': '处理失败'
        };
        return statusMap[status] || status;
    };

    // 获取文档图标
    const getDocumentIcon = (status: string): string => {
        const iconMap: { [key: string]: string } = {
            'pending': '⏳',
            'uploaded': '📄',
            'processing': '🔄',
            'completed': '✅',
            'failed': '❌'
        };
        return iconMap[status] || '📄';
    };

    // 计算文档总大小
    const formatTotalSize = (docs: DocumentRecord[]): string => {
        const totalBytes = docs.reduce((sum, doc) => sum + doc.file_size, 0);
        return formatFileSize(totalBytes);
    };

    // 查看文档详情
    const handleViewDocument = (documentId: string) => {
        // TODO: 实现文档详情查看功能
        alert(`查看文档: ${documentId}`);
    };

    return (
        <div className="pdf-upload-container">
            <div className="upload-section">
                <h2>PDF文档上传</h2>

                {/* 知识库选择 */}
                <div className="form-group">
                    <label>选择知识库：</label>
                    <select
                        value={selectedKnowledgeBase}
                        onChange={(e) => setSelectedKnowledgeBase(e.target.value)}
                        className="knowledge-base-select"
                    >
                        <option value="">请选择知识库...</option>
                        {knowledgeBases.map((kb) => (
                            <option key={kb.id} value={kb.id}>
                                📚 {kb.name} - {kb.subject} ({kb.category})
                            </option>
                        ))}
                    </select>

                    {/* 知识库统计信息 */}
                    {knowledgeBases.length > 0 && (
                        <div className="knowledge-base-stats">
                            <span className="stat-item">
                                📊 知识库总数: {knowledgeBases.length}
                            </span>
                            {selectedKnowledgeBase && (
                                <span className="stat-item">
                                    📄 文档总数: {documents.length}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* 文件上传区域 */}
                <div className="form-group">
                    <label>选择PDF文件：</label>
                    <div
                        className={`upload-area ${selectedFile ? 'has-file' : ''}`}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />

                        {selectedFile ? (
                            <div className="file-info">
                                <div className="file-icon">📄</div>
                                <div className="file-details">
                                    <div className="file-name">{selectedFile.name}</div>
                                    <div className="file-size">{formatFileSize(selectedFile.size)}</div>
                                </div>
                            </div>
                        ) : (
                            <div className="upload-placeholder">
                                <div className="upload-icon">📁</div>
                                <div className="upload-text">点击选择文件或拖拽PDF文件到此处</div>
                                <div className="upload-hint">支持PDF格式，最大10MB</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 描述输入 */}
                <div className="form-group">
                    <label>文档描述（可选）：</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="请输入文档的简要描述..."
                        className="description-input"
                        rows={3}
                    />
                </div>

                {/* 上传按钮 */}
                <button
                    onClick={handleUpload}
                    disabled={!selectedFile || !selectedKnowledgeBase || loading}
                    className="upload-btn"
                >
                    {loading ? '上传中...' : '开始上传'}
                </button>

                {/* 错误信息 */}
                {error && <div className="error-message">{error}</div>}

                {/* 成功信息 */}
                {success && <div className="success-message">{success}</div>}

                {/* 上传进度 */}
                {uploadProgress && (
                    <div className="progress-section">
                        <h3>上传进度</h3>
                        <div className="progress-bar">
                            <div
                                className="progress-fill"
                                style={{ width: `${uploadProgress.progress}%` }}
                            ></div>
                        </div>
                        <div className="progress-text">{uploadProgress.message}</div>
                    </div>
                )}
            </div>

            {/* 文档列表 */}
            {selectedKnowledgeBase && (
                <div className="documents-section">
                    <div className="documents-header">
                        <h3>📚 知识库文档列表</h3>
                        <div className="documents-summary">
                            <span className="summary-item">
                                📄 文档总数: {documents.length}
                            </span>
                            <span className="summary-item">
                                📊 总大小: {formatTotalSize(documents)}
                            </span>
                        </div>
                    </div>

                    {documents.length > 0 ? (
                        <div className="documents-list">
                            {documents.map((doc) => (
                                <div key={doc.id} className="document-item">
                                    <div className="document-icon">
                                        {getDocumentIcon(doc.status)}
                                    </div>
                                    <div className="document-info">
                                        <div className="document-name">{doc.filename}</div>
                                        <div className="document-meta">
                                            <span className="meta-item">
                                                📏 大小: {formatFileSize(doc.file_size)}
                                            </span>
                                            <span className={`meta-item status-${doc.status}`}>
                                                🔄 状态: {getStatusText(doc.status)}
                                            </span>
                                            {doc.processing_progress > 0 && (
                                                <span className="meta-item">
                                                    📈 进度: {doc.processing_progress}%
                                                </span>
                                            )}
                                        </div>
                                        <div className="document-date">
                                            📅 创建时间: {formatDate(doc.created_at)}
                                        </div>
                                    </div>
                                    <div className="document-actions">
                                        <button
                                            onClick={() => handleViewDocument(doc.id)}
                                            className="view-btn"
                                            title="查看文档详情"
                                        >
                                            👁️ 查看
                                        </button>
                                        <button
                                            onClick={() => handleDeleteDocument(doc.id)}
                                            className="delete-btn"
                                            title="删除文档"
                                        >
                                            🗑️ 删除
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-documents">
                            <div className="no-documents-icon">📭</div>
                            <p>该知识库暂无文档</p>
                            <p className="no-documents-hint">上传您的第一个PDF文档开始学习之旅！</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PDFUpload;
